import { fabric } from "fabric";
import { jsPDF as JsPDF } from "jspdf";
import { CanvasPaperConfig } from "../designer/PageDesigner/PageDesigner";
import { createPdfDocument } from "../designer/PageDesigner/pdf-document";
import { SidebarAction } from "../designer/types";
import {
  BorderedGroup,
  BorderedImage,
  removeOutlinedBox,
} from "./elements/bordered-objects";
import {
  FabricClearEvent,
  FabricEvents,
  FabricObject,
} from "./renderer/fabric-extended-types";
import { makeGroupClipPath } from "./renderer/fabric-file-renderer";
import { RenderContext, renderCanvas } from "./renderer/fabric-renderer";
import { modifyItem, orderItems, removeItem } from "./state/items";
import {
  DocumentDesignItemType,
  DocumentDesignState,
  FieldViewModes,
  isItemOfType,
} from "./state/types";
import { Table } from "./elements/table";
import { rearrangeItems } from "./relative-position";

export const gridSize = 10;

export interface CanvasManagerEvents {
  onStateChange: (state: DocumentDesignState) => void;
  onSelectionChange: (selectedId: string | undefined) => void;
  onReady: (manager: CanvasManager) => void;
  onAfterRender: () => void;
}

export class CanvasManager {
  public canvas: fabric.Canvas;
  private state!: DocumentDesignState;
  private events!: CanvasManagerEvents;
  private disposed = false;

  constructor(target: HTMLCanvasElement) {
    this.canvas = new fabric.Canvas(target);
    this.canvas.preserveObjectStacking = true;

    this.setGlobals();
    this.attachEvents();

    setTimeout(() => {
      if (this.disposed) {
        return;
      }
      // `setEvents` is a separate call, so the handlers may not be attached yet.
      this.events?.onReady(this);
    });
  }

  get isDisposed(): boolean {
    return this.disposed;
  }

  destroy(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    this.detachEvents();
    this.canvas.dispose();
  }

  render(state: DocumentDesignState, context: RenderContext): void {
    if (this.disposed) {
      return;
    }

    this.state = state;
    const renderedObjects = renderCanvas(this.canvas, state, context);

    const objectsMap = new Map<string, FabricObject>();
    renderedObjects.forEach((object) => objectsMap.set(object.id, object));

    let hasChangedState = false;
    const updatedItems = state.items.map((item) => {
      const isAbsolutePosition = item.position !== "anchored";

      if (!isAbsolutePosition) {
        const object = objectsMap.get(item.id);
        const renderedTop = Math.floor(
          object?.layoutTop ?? object?.top ?? item.top,
        );
        if (renderedTop !== item.top) {
          hasChangedState = true;
          return { ...item, top: renderedTop };
        }
      }

      return item;
    });

    if (hasChangedState) {
      this.events.onStateChange({ ...state, items: updatedItems });
    }
  }

  setEvents(events: CanvasManagerEvents): void {
    this.events = events;
    this.canvas.on(FabricEvents.afterRender, events.onAfterRender);
  }

  setIsPreviewing(isPreviewing: boolean): void {
    if (this.disposed) {
      return;
    }

    this.canvas.discardActiveObject();
    this.canvas.forEachObject((elem) => {
      elem.setOptions({ selectable: !isPreviewing });
    });
    this.canvas.hoverCursor = isPreviewing ? "default" : "move";
    this.canvas.requestRenderAll();
  }

  handleSidebarAction = (action: SidebarAction): void => {
    if (this.disposed) {
      return;
    }

    switch (action) {
      case SidebarAction.SendBackwards:
        this.canvas.sendBackwards(
          this.canvas.getActiveObject() as fabric.Object,
          true,
        );
        this.events.onStateChange(
          updateItemsOrderFromCanvas(this.state, this.canvas),
        );
        break;
      case SidebarAction.BringForward:
        this.canvas.bringForward(
          this.canvas.getActiveObject() as fabric.Object,
          true,
        );
        this.events.onStateChange(
          updateItemsOrderFromCanvas(this.state, this.canvas),
        );
        break;
      case SidebarAction.SendToBack:
        this.canvas.sendToBack(this.canvas.getActiveObject() as fabric.Object);
        this.events.onStateChange(
          updateItemsOrderFromCanvas(this.state, this.canvas),
        );
        break;
      case SidebarAction.BringToFront:
        this.canvas.bringToFront(
          this.canvas.getActiveObject() as fabric.Object,
        );
        this.events.onStateChange(
          updateItemsOrderFromCanvas(this.state, this.canvas),
        );
        break;
      case SidebarAction.removeSelection:
        this.canvas.discardActiveObject();
        this.canvas.requestRenderAll();
        break;
    }
  };

  async whenImagesLoaded(): Promise<void> {
    const loads = this.canvas
      .getObjects()
      .filter((obj): obj is BorderedImage => obj instanceof BorderedImage)
      .map((image) => image.whenLoaded());

    await Promise.all(loads);
  }

  toJsPDF(
    canvasPaperConfig: CanvasPaperConfig,
    callback: (doc: JsPDF) => void,
  ): void {
    if (this.disposed) {
      return;
    }

    this.setIsPreviewing(true);
    void createPdfDocument(this.canvas, canvasPaperConfig, callback);
    this.setIsPreviewing(false);
  }

  private setGlobals(): void {
    fabric.Object.prototype.set({
      borderScaleFactor: 2,
      cornerColor: "white",
      cornerStyle: "circle",
      cornerSize: 6,
      cornerStrokeColor: "#3A86FF",
      transparentCorners: false,
      objectCaching: false,
    });
  }

  private attachEvents(): void {
    this.canvas.on(FabricEvents.objectMoving, this.handleObjectMoving);
    this.canvas.on(FabricEvents.objectScaling, this.handleObjectScaling);
    this.canvas.on(FabricEvents.objectModified, this.handleObjectModified);
    this.canvas.on(FabricEvents.selectionCreated, this.handleSelectionCreated);
    this.canvas.on(FabricEvents.selectionUpdated, this.handleSelectionUpdated);
    this.canvas.on(FabricEvents.selectionCleared, this.handleSelectionCleared);
    this.canvas.on(FabricEvents.objectRemoved, this.handleObjectRemove);
    this.canvas.on(
      FabricEvents.objectRemoved,
      this.handleObjectRemoveClearBorders,
    );
    document.addEventListener("keydown", this.handleKeyDown);
  }

  private detachEvents(): void {
    this.canvas.off(FabricEvents.objectScaling, this.handleObjectScaling);
    this.canvas.off(FabricEvents.objectModified, this.handleObjectModified);
    this.canvas.off(FabricEvents.selectionCreated, this.handleSelectionCreated);
    this.canvas.off(FabricEvents.selectionUpdated, this.handleSelectionUpdated);
    this.canvas.off(FabricEvents.selectionCleared, this.handleSelectionCleared);
    this.canvas.off(FabricEvents.objectRemoved, this.handleObjectRemove);
    this.canvas.off(
      FabricEvents.objectRemoved,
      this.handleObjectRemoveClearBorders,
    );
    // Guarded rather than optional-chained: fabric's `off` drops every listener
    // for the event when the handler argument is undefined.
    if (this.events?.onAfterRender) {
      this.canvas.off(FabricEvents.afterRender, this.events.onAfterRender);
    }
    document.removeEventListener("keydown", this.handleKeyDown);
  }

  private readonly handleObjectMoving = (event: fabric.IEvent): void => {
    if (!event.target?.left || !event.target?.top) {
      return;
    }

    // Prevent moving on Y-axis if object is relative positioned
    const object = event.target as FabricObject;
    const item = this.state.items.find((item) => item.id === object.id);
    const isAbsolutePosition = item?.position !== "anchored";

    event.target.set({
      left: Math.round(event.target.left / gridSize) * gridSize,
      top: isAbsolutePosition
        ? Math.round(event.target.top / gridSize) * gridSize
        : item.top,
    });
  };

  private readonly handleObjectScaling = (event: fabric.IEvent): void => {
    const target = event.target as FabricObject;

    if (target?.width === undefined || target.height === undefined) {
      return;
    }

    const newWidth = target.width * (target.scaleX ?? 1);
    const newHeight = target.height * (target.scaleY ?? 1);

    target.width = newWidth;
    target.height = newHeight;
    target.scaleX = 1;
    target.scaleY = 1;
    target.dirty = true;
    target.objectCaching = false;

    if (target instanceof BorderedGroup) {
      target.clipPath = makeGroupClipPath(newWidth, newHeight);
    }
  };

  private readonly handleObjectModified = (event: fabric.IEvent): void => {
    let updatedState = this.state;
    if (event.target && event.target?.type !== "activeSelection") {
      updatedState = updateStateFromObject(
        this.state,
        event.target as FabricObject,
      );
    } else {
      const group = event.target as fabric.Group;
      const objects = group.getObjects() as FabricObject[];
      const ids = new Set();
      // Remove
      objects.forEach((elem) => {
        ids.add(elem.id);
        group.removeWithUpdate(elem);
      });

      // Update
      const updatedObjects = new Array<FabricObject>();
      this.canvas._objects.forEach((elem) => {
        if (ids.has((elem as FabricObject).id)) {
          updatedObjects.push(elem as FabricObject);
        }
      });
      updatedState = updateStateFromObjects(this.state, updatedObjects);
      // TODO: check what this does:
      // Add again
      // updatedObjects.forEach((elem) => {
      //   group.addWithUpdate(elem);
      // });
      this.events.onSelectionChange(undefined);
    }

    if (this.canvas.getObjects().length > 1) {
      updatedState = rearrangeItems(this.canvas, updatedState);
    }

    this.events.onStateChange(updatedState);
  };

  private readonly handleSelectionCreated = (event: FabricClearEvent): void => {
    if (event.selected && event.selected.length === 1) {
      const id = (event.selected[0] as FabricObject).id;
      this.events.onSelectionChange(id);
    } else {
      this.events.onSelectionChange(undefined);
    }
  };

  private readonly handleSelectionUpdated = (event: FabricClearEvent): void => {
    const id = (this.canvas._activeObject as FabricObject)?.id;
    if (id) {
      this.events.onSelectionChange(id);
    }
  };

  private readonly handleSelectionCleared = (): void => {
    this.events.onSelectionChange(undefined);
  };

  private readonly handleObjectRemove = (event: fabric.IEvent): void => {
    const id = (event.target as FabricObject)?.id;
    const item = this.state.items.find((item) => item.id === id);

    if (!item) {
      return;
    }

    // Prevent removing item when changing linked record view mode
    if (isItemOfType(item, DocumentDesignItemType.linkedRecord)) {
      const didChangeType =
        (event.target instanceof Table && item.mode !== FieldViewModes.table) ||
        (event.target instanceof fabric.Textbox &&
          item.mode === FieldViewModes.table);

      if (didChangeType) {
        return;
      }
    }

    this.events.onStateChange(removeItem(this.state, id));
  };

  readonly handleObjectRemoveClearBorders = (event: fabric.IEvent): void => {
    const element = event.target as FabricObject;
    if (!element.data) {
      return;
    }

    const { lineWidth } = element.data;
    if (lineWidth) {
      removeOutlinedBox(this.canvas, element.id);
    }
  };

  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    const activeObject = this.canvas.getActiveObject() as
      | FabricObject
      | undefined;
    const activeId = activeObject?.id;

    const isActiveElement =
      document.activeElement === document.body ||
      (document.activeElement instanceof HTMLDivElement &&
        document.activeElement.dataset.testid?.includes("scrollbar"));

    if (!activeObject || !activeId || !isActiveElement) {
      // ignore events if focus is outside canvas
      // `body` is focused when user is interacting with canvas
      return;
    }

    let updatedState = this.state;

    switch (event.key) {
      case "ArrowUp":
        updatedState = modifyItem(this.state, activeId, {
          top: (activeObject.top ?? 0) - gridSize,
        });
        event.preventDefault();
        break;
      case "ArrowDown":
        updatedState = modifyItem(this.state, activeId, {
          top: (activeObject.top ?? 0) + gridSize,
        });
        event.preventDefault();
        break;
      case "ArrowLeft":
        updatedState = modifyItem(this.state, activeId, {
          left: (activeObject.left ?? 0) - gridSize,
        });
        event.preventDefault();
        break;
      case "ArrowRight":
        updatedState = modifyItem(this.state, activeId, {
          left: (activeObject.left ?? 0) + gridSize,
        });
        event.preventDefault();
        break;
      case "Backspace":
      case "Delete":
        updatedState = removeItem(this.state, activeId);
        break;
      case "Escape":
        this.canvas.discardActiveObject();
        this.canvas.requestRenderAll();
        break;
    }

    this.events.onStateChange(updatedState);
  };
}

function updateStateFromObjects(
  state: DocumentDesignState,
  objects: FabricObject[],
): DocumentDesignState {
  let updatedState = state;
  objects.forEach((elem) => {
    updatedState = updateStateFromObject(updatedState, elem);
  });
  return updatedState;
}

function updateStateFromObject(
  state: DocumentDesignState,
  elem: FabricObject,
): DocumentDesignState {
  const id = elem.id;
  const returnState = modifyItem(state, id, {
    left: Math.ceil(elem.left ?? 0),
    top: Math.ceil(elem.top ?? 0),
    width: Math.ceil(elem.width ?? 1),
    height: Math.ceil(elem.height ?? 1),
    scaleX: 1,
    scaleY: 1,
    angle: Math.ceil(elem.angle ?? 0),
  });
  return returnState;
}

function updateItemsOrderFromCanvas(
  state: DocumentDesignState,
  canvas: fabric.Canvas,
): DocumentDesignState {
  const newOrderedIds: string[] = [];
  canvas.forEachObject((obj) => {
    newOrderedIds.push((obj as FabricObject).id);
  });
  return orderItems(state, newOrderedIds);
}
